import { useEffect, useRef, useState } from 'react';
import { partitionByManagerComplete } from './customGroupsHelpers';
import { toastSuccess, toastWarning } from '../components/popups/Toast';

// Owns the Pending / Ready partition of portfolios + per-fund mode +
// the Manager Complete static-data flag. Pushes mode changes for the
// currently-selected fund up to BuilderPage so the editor stays in sync.
//
// Inputs:
//   portfolios, template, remote, storage, completedKey
//   selectedFund, selectedGroup, setPortfolioMode
//   setRecentlyMovedGroup
//   setPortfoliosState                   — parent-owned mirror of pending pool
//   setReadyExpanded, setPendingExpanded — sidebar accordion expanders
//
// Returns: { pending, ready, videoModes, setVideoModes,
//            pendingRef, readyRef,
//            completed, completedDetails,
//            setContentType, moveToReady, moveToPending,
//            moveGroupToReady, moveGroupToPending,
//            markAsComplete }
export function usePortfolioPools({
    portfolios, template, remote, storage,
    completedKey,
    selectedFund, selectedGroup, setPortfolioMode,
    setRecentlyMovedGroup,
    setPortfoliosState,
    setReadyExpanded, setPendingExpanded,
}) {
    const [pending, setPending] = useState([]);
    const [ready, setReady] = useState([]);
    const [videoModes, setVideoModes] = useState({});
    const [completed, setCompleted] = useState(false);
    const [completedDetails, setCompletedDetails] = useState(null);

    const pendingRef = useRef(pending);
    pendingRef.current = pending;
    const readyRef = useRef(ready);
    readyRef.current = ready;

    // Re-entry lock for markAsComplete — its POSTs are fire-and-forget and the
    // Complete button's `completed` disable only lands after a re-render, so a
    // double-click could submit static-data/promote-commentary twice.
    const completingRef = useRef(false);

    const recentlyMovedTimerRef = useRef(null);
    useEffect(() => () => {
        if (recentlyMovedTimerRef.current) clearTimeout(recentlyMovedTimerRef.current);
    }, []);

    // Reset Pending pool to all portfolios on prop change (Ready pool is
    // overwritten in the load effect below once retrievev2 results come in).
    //
    // setPortfoliosState is a fresh arrow on every BuilderPage render —
    // including it in deps would cause an infinite render loop (effect
    // fires → setPortfoliosState → BuilderPage re-renders → new arrow
    // identity → effect re-fires). Fire only on `portfolios` change.
    useEffect(() => {
        if (portfolios && portfolios.length > 0) {
            setPending(portfolios);
            setPortfoliosState(portfolios);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [portfolios]);

    // Load each portfolio's stored contentType + managerComplete and
    // partition into Pending/Ready. Also load the "all commentaries complete"
    // static-data flag for the user's primary fund group.
    useEffect(() => {
        if (!portfolios || portfolios.length === 0) {
            setReady([]);
            return;
        }
        storage.getOrFetch('crm/me').then(me => {
            const myGroup = me.fundGroupAccess[0].groupId;  // we assume only 1 group access
            remote.get(`funds/portfolio/group/${myGroup}/static-data`).then(data => {
                const flag = (data || []).filter(dto => dto.key === completedKey);
                if (flag.length > 0 && flag[0].value == 'true') {
                    setCompleted(true);
                    setCompletedDetails({ user: flag[0].updatedBy, date: flag[0].updatedDate });
                } else {
                    setCompleted(false);
                }
            });
        });

        const lookups = portfolios.map(el => remote.get(`retrievev2/${btoa(el.fundNumber)}/${template}`));
        Promise.all(lookups).then(results => {
            const part = partitionByManagerComplete(portfolios, results);
            setVideoModes(part.videoModes);
            setReady(part.ready);
            setPending(part.pending);
            setPortfoliosState(part.pending);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [portfolios]);

    function setContentType(portfolio, type, e) {
        if (e) e.stopPropagation();
        remote.put(`savev2/${btoa(portfolio.fundNumber)}/${template}`, { contentType: type });
        setVideoModes(prev => ({ ...prev, [portfolio.fundNumber]: type }));
        if (setPortfolioMode) {
            const status = readyRef.current.some(p => p.fundNumber === portfolio.fundNumber) ? 'ready' : 'pending';
            setPortfolioMode({
                mode: type,
                fundNumber: portfolio.fundNumber,
                name: portfolio.fundName,
                status,
            });
        }
    }

    function moveToReady(portfolio) {
        remote.put(`savev2/${btoa(portfolio.fundNumber)}/${template}`, { managerComplete: 'ready' });
        const newPending = pending.filter(p => p.fundNumber !== portfolio.fundNumber);
        const newReady = ready.length > 0
            ? [...ready, portfolio].sort((a, b) => a.fundName.localeCompare(b.fundName))
            : [portfolio];
        setPending(newPending);
        setPortfoliosState(newPending);
        setReady(newReady);
        if (setReadyExpanded) setReadyExpanded(true);
        if (selectedFund?.fundNumber === portfolio.fundNumber && !selectedGroup && setPortfolioMode) {
            setPortfolioMode({
                mode: videoModes[portfolio.fundNumber] || 'editor',
                fundNumber: portfolio.fundNumber,
                name: portfolio.fundName,
                status: 'ready',
            });
        }
    }

    function moveToPending(portfolio) {
        remote.put(`savev2/${btoa(portfolio.fundNumber)}/${template}`, { managerComplete: 'pending' });
        const newReady = ready.filter(p => p.fundNumber !== portfolio.fundNumber);
        const newPending = pending.length > 0
            ? [...pending, portfolio].sort((a, b) => a.fundName.localeCompare(b.fundName))
            : [portfolio];
        setPending(newPending);
        setPortfoliosState(newPending);
        setReady(newReady);
        if (setPendingExpanded) setPendingExpanded(true);
        if (completed) markAsComplete(false);
        if (selectedFund?.fundNumber === portfolio.fundNumber && !selectedGroup && setPortfolioMode) {
            setPortfolioMode({
                mode: videoModes[portfolio.fundNumber] || 'editor',
                fundNumber: portfolio.fundNumber,
                name: portfolio.fundName,
                status: 'pending',
            });
        }
    }

    function moveGroupToReady(group, onSelectionResync) {
        const members = pending.filter(p => group.fundNumbers.includes(p.fundNumber));
        if (members.length === 0) return;
        const memberNumbers = new Set(members.map(p => p.fundNumber));
        members.forEach(p => {
            remote.put(`savev2/${btoa(p.fundNumber)}/${template}`, { managerComplete: 'ready' });
        });
        const newPending = pending.filter(p => !memberNumbers.has(p.fundNumber));
        const newReady = [...ready, ...members].sort((a, b) => a.fundName.localeCompare(b.fundName));
        setPending(newPending);
        setPortfoliosState(newPending);
        setReady(newReady);
        if (setReadyExpanded) setReadyExpanded(true);
        if (setRecentlyMovedGroup) {
            setRecentlyMovedGroup(group.id);
            if (recentlyMovedTimerRef.current) clearTimeout(recentlyMovedTimerRef.current);
            recentlyMovedTimerRef.current = setTimeout(() => {
                recentlyMovedTimerRef.current = null;
                setRecentlyMovedGroup(v => v === group.id ? null : v);
            }, 1800);
        }
        if (onSelectionResync) onSelectionResync({ status: 'ready', newPool: newReady });
    }

    function moveGroupToPending(group, onSelectionResync) {
        const members = ready.filter(p => group.fundNumbers.includes(p.fundNumber));
        if (members.length === 0) return;
        const memberNumbers = new Set(members.map(p => p.fundNumber));
        members.forEach(p => {
            remote.put(`savev2/${btoa(p.fundNumber)}/${template}`, { managerComplete: 'pending' });
        });
        const newReady = ready.filter(p => !memberNumbers.has(p.fundNumber));
        const newPending = [...pending, ...members].sort((a, b) => a.fundName.localeCompare(b.fundName));
        setPending(newPending);
        setPortfoliosState(newPending);
        setReady(newReady);
        if (setPendingExpanded) setPendingExpanded(true);
        if (completed) markAsComplete(false);
        if (onSelectionResync) onSelectionResync({ status: 'pending', newPool: newPending });
    }

    function markAsComplete(value) {
        if (completingRef.current) return;
        // Re-assert the Complete gate (complete direction only — the internal
        // markAsComplete(false) revert must always run): the shared Button's "disabled"
        // is CSS-only (pointer-events blocks the mouse but not keyboard activation — no
        // native disabled attribute), so the handler must enforce all-portfolios-Ready itself.
        if (value && (pendingRef.current.length > 0 || readyRef.current.length === 0 || completed)) return;
        completingRef.current = true;
        storage.getOrFetch('crm/me').then(me => {
            const posts = [];
            me.fundGroupAccess.forEach(myGroupObj => {
                const myGroup = myGroupObj.groupId;
                const payload = { groupId: myGroup, key: completedKey, value };
                posts.push(remote.post(`funds/portfolio/group/${myGroup}/static-data`, [payload]).then(loaded => {
                    const flag = (loaded || []).filter(dto => dto.key === completedKey);
                    // server may return value as string OR boolean; loose compare on purpose
                    if (flag.length > 0 && flag[0].value == 'true') {
                        setCompletedDetails({ user: flag[0].updatedBy, date: flag[0].updatedDate });
                    }
                }));
                posts.push(remote.post(`funds/portfolio/group/${myGroup}/promote-commentary`, [payload]));
            });
            return Promise.allSettled(posts);
        }).finally(() => {
            completingRef.current = false;
        });
        if (value) {
            toastSuccess('Thank you.  Colonial First State has been notified that you have completed your commentaries.');
            setCompleted(true);
        } else {
            toastWarning('Your commentaries are no longer marked as complete.  They will not be included unless you mark them as complete again');
            setCompleted(false);
        }
    }

    return {
        pending, ready, videoModes, setVideoModes,
        pendingRef, readyRef,
        completed, completedDetails,
        setContentType, moveToReady, moveToPending,
        moveGroupToReady, moveGroupToPending,
        markAsComplete,
    };
}
